
import React, { createContext, useContext, useEffect, useState } from 'react';
import { ImpersonationUser, checkIsAdmin } from '@/services/admin';
import { useAuth } from './AuthContext';

type ImpersonationContextType = {
  isAdmin: boolean;
  isImpersonating: boolean;
  impersonatedUser: ImpersonationUser | null;
  startImpersonation: (user: ImpersonationUser) => void;
  stopImpersonation: () => void;
};

const ImpersonationContext = createContext<ImpersonationContextType>({
  isAdmin: false,
  isImpersonating: false,
  impersonatedUser: null,
  startImpersonation: () => {},
  stopImpersonation: () => {}
});

export const useImpersonation = () => useContext(ImpersonationContext);

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonationUser | null>(null);

  // Check if the user is an admin when they log in
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const adminStatus = await checkIsAdmin();
          console.log("Admin status check:", adminStatus);
          setIsAdmin(adminStatus);
          
          // If the user isn't an admin anymore, stop any impersonation
          if (!adminStatus && isImpersonating) {
            stopImpersonation();
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
          setIsAdmin(false);
          if (isImpersonating) {
            stopImpersonation();
          }
        }
      } else {
        setIsAdmin(false);
        if (isImpersonating) {
          stopImpersonation();
        }
      }
    };

    checkAdminStatus();
  }, [user, isImpersonating]);

  const startImpersonation = (user: ImpersonationUser) => {
    if (isAdmin) {
      console.log("Starting impersonation for user:", user);
      // Clear any existing state first
      setTimeout(() => {
        setImpersonatedUser(user);
        setIsImpersonating(true);
        // Store impersonation state in sessionStorage for persistence across page refreshes
        sessionStorage.setItem('impersonatedUser', JSON.stringify(user));
      }, 0);
    } else {
      console.warn("Non-admin attempting to impersonate:", user);
    }
  };

  const stopImpersonation = () => {
    console.log("Stopping impersonation");
    setImpersonatedUser(null);
    setIsImpersonating(false);
    // Remove from sessionStorage
    sessionStorage.removeItem('impersonatedUser');
  };

  // Restore impersonation state from sessionStorage on page load
  useEffect(() => {
    if (isAdmin) {
      const storedUser = sessionStorage.getItem('impersonatedUser');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser) as ImpersonationUser;
          console.log("Restoring impersonation from session storage:", parsedUser);
          setImpersonatedUser(parsedUser);
          setIsImpersonating(true);
        } catch (e) {
          console.error("Error parsing impersonated user from sessionStorage:", e);
          sessionStorage.removeItem('impersonatedUser');
        }
      }
    }
  }, [isAdmin]);

  return (
    <ImpersonationContext.Provider value={{
      isAdmin,
      isImpersonating,
      impersonatedUser,
      startImpersonation,
      stopImpersonation
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};
